import React, { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../api";
import { useAuth } from "../../AuthContext";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";

const AUTOPLAY_MS = 4500;
const SWIPE_THRESHOLD_PX = 40;
const SLIDE_COUNT = 2;

// A rotating two-slide banner on Home (both Owner and Tenant) — one slide
// teases the upcoming e-stamped rental agreement feature and lets an owner
// register interest, the other promotes the existing refer-a-friend reward.
// Auto-advances, but any manual interaction (dot tap or swipe) restarts the
// timer from zero so it doesn't immediately snap away under the user's thumb.
export function HomePromoCarousel({ onNavigate }: { onNavigate: (view: "refer") => void }) {
  const { user, updateUser } = useAuth();
  const toast = useToast();
  const { t } = useLanguage();
  const [active, setActive] = useState(0);
  const [registering, setRegistering] = useState(false);
  const [trackWidth, setTrackWidth] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Pixel offsets, not percentages: a percentage translateX on this track
  // got visibly stuck mid-transition when animating back toward slide 0 in
  // testing (transform stopped updating even though the inline style string
  // was changing) — pixel values animate reliably, so measure the
  // carousel's own width instead of relying on the transform's % reference.
  const carouselRef = useRef<HTMLDivElement | null>(null);

  function restartTimer() {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive((i) => (i + 1) % SLIDE_COUNT);
    }, AUTOPLAY_MS);
  }

  useEffect(() => {
    restartTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const measure = () => setTrackWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  function goTo(i: number) {
    setActive(i);
    restartTimer();
  }

  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    goTo(delta < 0 ? (active + 1) % SLIDE_COUNT : (active - 1 + SLIDE_COUNT) % SLIDE_COUNT);
  }

  async function registerInterest() {
    if (user?.estampInterestAt || registering) return;
    setRegistering(true);
    try {
      const { user: updated } = await api.post("/api/auth/estamp-interest", {});
      updateUser(updated);
      toast(t("homePromo.estampToast"));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setRegistering(false);
    }
  }

  return (
    <>
      <div className="promo-carousel" ref={carouselRef}>
        <div className="promo-track" style={{ transform: `translateX(-${active * trackWidth}px)` }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <div className="promo-slide estamp">
            <div className="promo-slide-icon">📜</div>
            <div className="promo-slide-text">
              <div className="promo-slide-title">
                <span className="promo-highlight">{t("homePromo.estampTitleHighlight")}</span> {t("homePromo.estampTitle")}
              </div>
              <div className="promo-slide-sub">{t("homePromo.estampSub")}</div>
            </div>
            {user?.role === "owner" && (
              <button className="promo-slide-cta" disabled={!!user?.estampInterestAt || registering} onClick={registerInterest}>
                {user?.estampInterestAt
                  ? t("homePromo.estampCtaDone")
                  : registering
                  ? t("common.saving")
                  : t("homePromo.estampCta")}
              </button>
            )}
          </div>
          <div className="promo-slide refer">
            <div className="promo-slide-icon">🎁</div>
            <div className="promo-slide-text">
              <div className="promo-slide-title">{t("homePromo.referTitle")}</div>
              <div className="promo-slide-sub">{t("homePromo.referSub")}</div>
            </div>
            <button className="promo-slide-cta" onClick={() => onNavigate("refer")}>
              {t("homePromo.referCta")}
            </button>
          </div>
        </div>
      </div>
      <div className="promo-dots">
        {Array.from({ length: SLIDE_COUNT }).map((_, i) => (
          <button key={i} className={`promo-dot${active === i ? " active" : ""}`} aria-label={`Slide ${i + 1}`} onClick={() => goTo(i)} />
        ))}
      </div>
    </>
  );
}
